/* External */
import { useCallback, memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { getUnixTime } from 'date-fns';

/* Components */
import SearchCard from '../SearchCard';

/* Providers */
import { useUserAccount } from '../../providers/UserProvider';

/* Utils */
import { noOp } from '../../utils/functions';
import { gaService } from '../../services/gaService';
import {
  isContent, isPlayingNow, isSchedule, isShow, isSports,
} from '../../utils/metaDataUtils';
import { redirectToDetail, redirectToPlayer } from '../../utils/history';
import { REPORT_GA, PageType } from '../../utils/constants';
import { PLAY_SOURCE_PAGE, PLAY_SOURCE_SECTION } from '../../utils/trackSourceConviva';
import {
  formatContentData,
  playerType,
  transmissionContentType,
} from '../../utils/googleTags';
import { Content, Schedule, Show } from '../../storage/models';
import { isLearnActionV4Active } from '../../utils/env';

/* Hooks */
import useContent from '../../hooks/useContent';
import useFetchSearchResult from '../../hooks/useFetchSearchResult';
import useFetchClickAction from '../../hooks/useFetchClickAction';

function SearchCardContainer({
  result,
  index,
  handleSearchResultTagEvent = noOp,
  querySearch = '',
  onArrowPress = noOp,
}) {
  const { fetchSearchResult } = useFetchSearchResult();
  const isScheduleFlag = isSchedule(result);
  const { fetchClickAction } = useFetchClickAction();

  const {
    msoProvider,
    accountType: userType,
  } = useUserAccount() || {};

  const content = useContent(isScheduleFlag ? result.content : undefined) || result;

  const searchResultParams = useMemo(() => ({
    actionTime: getUnixTime(new Date()),
    contentSourceId: content?.contentSourceId,
    vmsId: content?.vmsId,
    vrioAssetId: content?.vrioAssetId,
    actionOrigin: content?.actionOrigin || '',
    userOrigin: {
      playSource: PLAY_SOURCE_PAGE[PageType.SEARCH],
      userLogin: userType,
      msoProvider,
    },
  }), [content, msoProvider, userType]);

  const clickActionParams = useMemo(() => ({
    actionOrigin: content?.actionOrigin || '',
    userOrigin: {
      playSource: PLAY_SOURCE_PAGE[PageType.SEARCH],
      userLogin: userType,
      msoProvider,
    },
    contentType: content.isLive ? 1 : 2,
    guideId: content?.streaming?.id || content?.vrioAssetId || '',
    vrioAssetId: content.vrioAssetId,
  }), [content, msoProvider, userType]);

  const handleSearchResultsEnterPress = useCallback((
    searchResult,
    searchResultIndex,
  ) => {
    const { sourceProvider, schema, type } = searchResult;
    const locationState = {
      actionOrigin: content?.actionOrigin || '',
      transmissionType: transmissionContentType(schema),
      sectionName: 'search',
      componentType: 'search',
      gtmPlaySource: 'search',
    };
    const contentType = type || schema;

    handleSearchResultTagEvent({
      data: {
        ...locationState,
        listPosition: searchResultIndex + 1,
        isSports: isSports(searchResult),
        playerType: playerType(contentType, isSchedule(searchResult), isSports(searchResult)),
        content: {
          ...formatContentData({
            content: searchResult,
            rest: {
              playSource: locationState.gtmPlaySource,
              isLive: isSchedule(searchResult),
            },
          }),
        },
      },
    });
    if (isContent(searchResult) || isShow(searchResult)) {
      gaService.sendEvent({
        action: REPORT_GA.GA_EVENT_SEARCH_CONVERSION_PDP,
        label: querySearch,
        category: REPORT_GA.GA_EVENT_CATEGORY_SEARCH,
      }, { sourceProvider });
      redirectToDetail(searchResult, { state: locationState });
    } else if (isSchedule(searchResult)) {
      if (isPlayingNow(searchResult)) {
        gaService.sendEvent({
          action: REPORT_GA.GA_EVENT_SEARCH_CONVERSION_PLAY,
          label: querySearch,
          category: REPORT_GA.GA_EVENT_CATEGORY_SEARCH,
        }, { sourceProvider });
        redirectToPlayer(searchResult, {
          page: PLAY_SOURCE_PAGE[PageType.SEARCH],
          section: PLAY_SOURCE_SECTION.LIVE_CAROUSEL,
          state: locationState,
        });
      } else {
        gaService.sendEvent({
          action: REPORT_GA.GA_EVENT_SEARCH_CONVERSION_PDP,
          label: querySearch,
          category: REPORT_GA.GA_EVENT_CATEGORY_SEARCH,
        }, { sourceProvider });
        redirectToDetail(searchResult, { state: locationState });
      }
    }
  }, [handleSearchResultTagEvent, querySearch, content]);

  const handleEnterPress = useCallback(() => {
    if (isLearnActionV4Active) {
      fetchSearchResult(searchResultParams);
    }

    fetchClickAction(clickActionParams);

    handleSearchResultsEnterPress(result, index);
  }, [
    handleSearchResultsEnterPress,
    fetchSearchResult,
    fetchClickAction,
    searchResultParams,
    clickActionParams,
    result,
    index,
  ]);

  return (
    <SearchCard
      result={result}
      onEnterPress={handleEnterPress}
      onArrowPress={onArrowPress}
    />
  );
}

SearchCardContainer.propTypes = {
  index: PropTypes.number.isRequired,
  result: PropTypes.oneOfType([
    PropTypes.instanceOf(Schedule),
    PropTypes.instanceOf(Content),
    PropTypes.instanceOf(Show),
  ]).isRequired,
  querySearch: PropTypes.string,
  handleSearchResultTagEvent: PropTypes.func,
  onArrowPress: PropTypes.func,
};

export default memo(SearchCardContainer);
